<template>
    <div class="checkbox-group">
        <div
            v-for="option in options"
            :key="option.value"
            class="form-check d-flex align-items-center"
        >
            <input
                class="form-check-input"
                :class="{ 'input-invalid': isInvalid }"
                :style="{ cursor: 'pointer' }"
                type="checkbox"
                :name="name"
                :value="option.value"
                v-model="localValue"
                :disabled="option?.disabled || disabled"
                @change="localValueChange"
                :id="`${name}-${option.value}`"
            />
            <label :for="`${name}-${option.value}`" class="form-check-label">{{
                option.label
            }}</label>
        </div>
        <!-- Hidden input for native validation -->
        <input
            ref="hiddenInput"
            type="text"
            v-if="required"
            :required="required"
            :value="localValue.length > 0 ? 'selected' : ''"
            tabindex="-1"
            aria-hidden="true"
            style="
                opacity: 0;
                position: absolute;
                z-index: -1;
                width: 0;
                height: 0;
            "
            @invalid.prevent="handleInvalid"
        />
        <div v-if="isInvalid" class="font-note" style="color: var(--danger)">
            {{ displayError }}
        </div>
    </div>
</template>

<script>
import { normalizeExclusiveNASelection } from '@/utils/auditFieldApplicability';

export default {
    name: 'CheckboxGroup',
    props: {
        modelValue: {
            type: Array,
            default: () => [],
        },
        options: {
            type: Array,
            required: true,
            validator: (value) =>
                value.every((opt) => 'value' in opt && 'label' in opt),
        },
        name: {
            type: String,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: undefined,
        },
        required: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isInvalid: false,
            pendingValue: null,
        };
    },
    computed: {
        displayError() {
            return this.error ?? this.$t('validation.selectAtLeastOne');
        },

        localValue: {
            get() {
                return this.modelValue;
            },
            set(value) {
                const normalizedValue = normalizeExclusiveNASelection({
                    previousValues: this.modelValue,
                    nextValues: value,
                    options: this.options,
                });
                this.pendingValue = normalizedValue;
                this.$emit('update:modelValue', normalizedValue);
            },
        },
    },
    emits: ['update:modelValue', 'change', 'remove-option'],
    methods: {
        localValueChange() {
            if (!this.disabled) {
                const value = this.pendingValue ?? this.localValue;
                this.pendingValue = null;
                this.isInvalid = this.required && value.length === 0;
                this.$emit('change', value);
            }
        },
        handleInvalid() {
            this.isInvalid = true;
        },
        removeOption(optionToRemove) {
            if (this.options.length <= 1) return;
            this.$emit('remove-option', optionToRemove);
        },
    },
};
</script>

<style scoped>
.checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem 1rem;
    flex-wrap: wrap;
}

.input-invalid {
    border: 1.5px solid var(--danger) !important;
}
</style>
